/**
 * English interface strings — the complete dictionary.
 *
 * Source of truth for keys: `translate` falls back to `en[key]` for any
 * string missing from another language, then to the key itself. Setup and
 * lookup logic lives in `i18n.ts`.
 */

type Dict = Record<string, string>

export const en: Dict = {
  /* --- common ------------------------------------------------------------ */
  'common.saveChanges': 'Save changes',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.saved': 'Saved',
  'common.add': 'Add',
  'common.adding': 'Adding…',
  'common.delete': 'Delete',
  'common.remove': 'Remove',
  'common.edit': 'Edit',
  'common.close': 'Close',
  'common.closePanel': 'Close panel',
  'common.dismiss': 'Dismiss',
  'common.export': 'Export',
  'common.exportCsv': 'Export CSV',
  'common.viewAll': 'View all',
  'common.tryAgain': 'Try again',
  'common.retry': 'Retry',
  'common.loading': 'Loading…',
  'common.loadingPeople': 'Loading people…',
  'common.none': 'None',
  'common.never': 'Never',
  'common.search': 'Search',
  'common.searchEllipsis': 'Search…',
  'common.clearSearch': 'Clear search',
  'common.clear': 'Clear',
  'common.clearFilters': 'Clear filters',
  'common.beta': 'Beta',
  'common.pdf': 'PDF',
  'common.connect': 'Connect',
  'common.disconnect': 'Disconnect',
  'common.reconnect': 'Reconnect',
  'common.active': 'Active',
  'common.restore': 'Restore',
  'common.archive': 'Archive',
  'common.archived': 'archived',
  'common.confirm': 'Confirm',
  'common.signIn': 'Sign in',
  'common.or': 'or',
  'common.couldNotSave': 'Could not save',
  'common.couldNotSaveBody': 'Your changes were not stored. Try again.',
  'common.somethingWentWrong': 'Something went wrong. Try again.',

  /* --- time ------------------------------------------------------------- */
  'time.today': 'Today',
  'time.yesterday': 'Yesterday',
  'time.tomorrow': 'Tomorrow',
  'time.ago': '{value} ago',

  /* --- navigation ----------------------------------------------------------- */
  'nav.section.insights': 'Insights',
  'nav.section.workspace': 'Workspace',
  'nav.section.admin': 'Administration',
  'nav.item.dashboard': 'Home',
  'nav.item.meetings': 'Meetings',
  'nav.item.actions': 'Action items',
  'nav.item.ask': 'Ask Sabha',
  'nav.item.search': 'Search',
  'nav.item.analytics': 'Analytics',
  'nav.item.integrations': 'Integrations',
  'nav.item.settings': 'Settings',
  'nav.item.tenants': 'Tenants',
  'nav.item.audit': 'Audit log',
  'nav.item.vocabulary': 'Vocabulary',
  'nav.item.feedbackInbox': 'Feedback inbox',
  'nav.collapseSidebar': 'Collapse sidebar',
  'nav.expandSidebar': 'Expand sidebar',
  'nav.openNavigation': 'Open navigation',
  'nav.skipToContent': 'Skip to content',
  'nav.primary': 'Primary',
  'nav.restoringSession': 'Restoring your session',

  /* --- topbar / menus ------------------------------------------------------- */
  'topbar.searchWide': 'Search meetings, people, decisions…',
  'palette.placeholder': 'Search meetings or jump to a page…',
  'palette.inputAria': 'Command palette input',
  'palette.dialogLabel': 'Command palette',
  'palette.noMatches': 'No matches',
  'palette.group.navigate': 'Navigate',
  'palette.group.meetings': 'Meetings',
  'palette.group.search': 'Search',
  'palette.goTo': 'Go to {label}',
  'palette.searchFor': 'Search transcripts for “{term}”',
  'topbar.home': '{app} home',
  'userMenu.accountMenu': 'Account menu',
  'userMenu.yourProfile': 'Your profile',
  'userMenu.workspaceSettings': 'Workspace settings',
  'userMenu.appearance': 'Appearance: {theme}',
  'userMenu.contactSupport': 'Contact support',
  'userMenu.signOut': 'Sign out',
  'support.title': 'Contact support',
  'support.intro': 'Tell us what went wrong. Your message reaches the Sabha team together with your name and workspace.',
  'support.subject.label': 'Subject',
  'support.subject.placeholder': 'Meeting summary is missing',
  'support.issue.label': 'Describe your issue *',
  'support.issue.placeholder': 'Describe what went wrong, what you expected to happen, and when the issue occurred.',
  'support.attach.label': 'Attach screenshots (optional)',
  'support.attach.hint': 'PNG, JPG or WebP. Up to {count} files, {file} MB each and {total} MB in total.',
  'support.attach.button': 'Choose files',
  'support.attach.remove': 'Remove {name}',
  'support.submit': 'Submit Request',
  'support.sending': 'Sending…',
  'support.success.title': 'Request sent',
  'support.success.body': 'The Sabha team has your request and will reply by email.',
  'support.error.title': 'Your request was not sent',
  'support.error.titleUnconfirmed': "We couldn't confirm whether your request was sent",
  'support.error.generic': 'Something went wrong while sending your request. Please try again.',
  'support.error.notConfigured': 'Support requests are not available right now.',
  'support.error.unavailable': 'Support could not be reached right now. Please try again in a moment.',
  'support.error.sendFailed': 'We could not send your request. Please try again.',
  'support.error.sendUnconfirmed':
    'We could not confirm whether your request was sent. It may already have reached us, so please check with your team before sending it again. If you do not hear back within a few hours, contact us again.',
  'support.error.emailFallback': 'Or email us at {address}.',
  'support.error.rateLimited': 'You have reached the limit for support requests. Try again in about {minutes} min.',
  'support.error.rateLimitedNoWait': 'You have reached the limit for support requests. Try again later.',
  'support.error.unreadable': 'We could not read your request. Check your screenshots and try again.',
  'support.error.tooLarge': 'The request is too large. Attach fewer or smaller screenshots.',
  'support.error.invalidCharacters': 'The text contains characters that are not allowed.',
  'support.error.subject.required': 'Enter a subject.',
  'support.error.subject.tooLong': 'The subject must be {max} characters or fewer.',
  'support.error.description.required': 'Describe your issue.',
  'support.error.description.tooLong': 'The description must be {max} characters or fewer.',
  'support.error.file.unsupported': 'Only PNG, JPG and WebP images can be attached.',
  'support.error.file.empty': 'This file is empty.',
  'support.error.file.tooLarge': 'Each screenshot must be {max} MB or smaller.',
  'support.error.file.tooMany': 'You can attach at most {max} screenshots.',
  'support.error.file.totalTooLarge': 'The screenshots together must be {total} MB or smaller.',
  'theme.heading': 'Appearance',
  'theme.change': 'Change appearance',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.system': 'Match system',
  'theme.value.light': 'light',
  'theme.value.dark': 'dark',
  'theme.value.system': 'system',
  'tenant.workspace': 'Workspace',
  'notifications.title': 'Notifications',
  'notifications.withUnread': 'Notifications ({count} unread)',
  'notifications.caughtUpTitle': 'You are all caught up',
  'notifications.caughtUpBody': 'Summaries, assignments and recording failures land here.',
  'notifications.markAllRead': 'Mark all read',
  'notifications.summaryReady.body': 'The summary and action items are ready to read.',
  'notifications.leadIn.recordingFailed': 'Recording failed',
  'notifications.leadIn.recordingStopped': 'Recording stopped',
  'notifications.leadIn.botNotAdmitted': 'Bot not admitted',
  'notifications.leadIn.accessDenied': 'Access denied',
  'notifications.leadIn.summaryReady': 'Summary ready',
  'notifications.recordingFailure.botNotAdmitted':
    "The bot wasn't admitted to the meeting in time.",
  'notifications.recordingFailure.botDenied':
    'Sabha AI was denied access to the meeting.',
  'notifications.recordingFailure.meetingCancelled':
    'The meeting was cancelled before the recording could start.',
  'notifications.recordingFailure.botRemoved':
    'The recording stopped because the bot was removed from the meeting.',
  'notifications.recordingFailure.platformUnreachable':
    "Sabha AI couldn't join the meeting. Please try again.",
  'notifications.recordingFailure.genericError':
    'Recording failed due to an unexpected error.',
  'notifications.recordingFailure.unknownFailure':
    "We couldn't complete the recording. Please try again.",

  /* --- QueryBoundary / EmptyState defaults -------------------------------- */
  'query.loadFailTitle': 'We could not load this',
  'query.noAccessTitle': 'You do not have access to this',
  'query.loadFailBody': 'The request did not reach the server. Check your connection and try again.',
  'query.noAccessBody': 'Ask a workspace admin to grant you access.',
  'query.nothingHere': 'Nothing here yet',

  /* --- pagination ------------------------------------------------------- */
  'pagination.noResults': 'No results',
  'pagination.range': '{from}–{to} of {total}',
  'pagination.previous': 'Previous',
  'pagination.next': 'Next',

  /* --- settings shell -------------------------------------------------------- */
  'settings.title': 'Settings',
  'settings.description': 'Workspace configuration, people and data controls.',
  'settings.nav': 'Settings',
  'settings.tab.profile': 'Your profile',
  'settings.tab.workspace': 'Workspace',
  'settings.tab.members': 'Members',
  'settings.tab.roles': 'Roles',
  'settings.tab.patrons': 'Patrons',
  'settings.tab.recording': 'Recording rules',
  'settings.tab.languages': 'Languages',
  'settings.tab.notifications': 'Notifications',
  'settings.tab.billing': 'Billing',
  'settings.tab.security': 'Security & data',

  /* --- profile pane ------------------------------------------------------- */
  'profile.card.title': 'Your profile',
  'profile.card.description': 'How you appear to others in this workspace.',
  'profile.field.name': 'Full name',
  'profile.field.email': 'Work email',
  'profile.field.email.hint': 'Managed by your identity provider.',
  'profile.field.timezone': 'Time zone',
  'profile.field.timezone.hint': 'Every time in the app is rendered in this zone.',
  'profile.field.language': 'Interface language',
  'profile.field.language.hint': 'Does not change transcription languages.',
  'profile.personalEmail.title': 'Personal email',
  'profile.personalEmail.description': 'What Sabha sends you directly.',
  'profile.dailyDigest.label': 'Daily digest of meetings you missed',
  'profile.dailyDigest.description': 'One email at 08:00 in your time zone.',
  'profile.actionAssigned.label': 'Sabha Catchup: my open action items',
  'profile.actionAssigned.description': 'One email on weekday evenings (18:00 in your time zone) with your open action items, what is new, and meetings you organised that need an owner. Skipped when there is nothing to report.',

  /* --- dashboard --------------------------------------------------------- */
  'dashboard.greeting': 'Good day, {name}',
  'dashboard.greetingFallback': 'there',
  'dashboard.subtitle': 'Your workspace at a glance.',
  'dashboard.askSabha': 'Ask Sabha',
  'dashboard.recordingNow': 'Recording now',
  'dashboard.startedAt': 'started {time}',
  'dashboard.stat.meetingsRecorded': 'Meetings recorded',
  'dashboard.stat.hoursCaptured': 'Hours captured',
  'dashboard.stat.actionItemsOpen': 'Action items open',
  'dashboard.stat.avgMeeting': 'Avg meeting',
  'dashboard.stat.last30': 'vs previous 30 days',
  'dashboard.recentMeetings': 'Recent meetings',
  'dashboard.noMeetingsTitle': 'No meetings yet',
  'dashboard.noMeetingsBody': 'Invite the recorder to a meeting on your calendar and it will show up here.',
  'dashboard.yourActionItems': 'Your action items',
  'dashboard.seeAll': 'See all',
  'dashboard.nothingAssigned': 'Nothing assigned to you',
  'dashboard.recordingVolume': 'Recording volume',
  'dashboard.recordingVolumeDesc': 'Minutes captured per day, last 30 days.',
  'dashboard.upNext': 'Up next',
  'dashboard.upNextDesc': 'Meetings the recorder will join.',
  'dashboard.checkedRelative': 'Checked {when}',
  'dashboard.checkForInvites': 'Check the calendar for new invitations',
  'dashboard.refreshUpcoming': 'Refresh upcoming meetings',
  'dashboard.badge.joining': 'Joining',
  'dashboard.badge.claimed': 'Claimed',
  'dashboard.calendarNotConnected': 'Calendar not connected',
  'dashboard.calendarNotConnectedBody': 'This deployment cannot read the recorder’s calendar, so only meetings it has already claimed can be listed.',
  'dashboard.calendarUnreachable': 'Could not reach the calendar',
  'dashboard.calendarUnreachableBody': 'Nothing is missing from your diary — we just could not read it. Try refreshing.',
  'dashboard.nothingScheduled': 'Nothing scheduled',
  'dashboard.calendarClear': 'Your calendar is clear.',
  'dashboard.staleCalendar': 'The calendar was unreachable, so this list may be out of date.',

  /* --- meetings list ---------------------------------------------------------- */
  'meetings.title': 'Meetings',
  'meetings.description': 'Every meeting the recorder was invited to, including the ones it could not record.',
  'meetings.recordMeeting': 'Record a meeting',
  'meetings.recordNow': 'Record now',
  'meetings.searchPlaceholder': 'Search by title, participant or meeting ID…',
  'meetings.searchAria': 'Search meetings',
  'meetings.noMatchTitle': 'No meetings match these filters',
  'meetings.noMatchBody': 'Invite sabha.ai@pssgway.com to a meeting and it will appear here about two minutes before it starts.',
  'meetings.filter.status': 'Status',
  'meetings.filters': 'Filters',
  'meetings.filter.apply': 'Apply',
  'meetings.filter.clearAll': 'Clear all',
  'meetings.filter.organizer': 'Organiser',
  'meetings.filter.participants': 'Participants',
  'meetings.filter.namePlaceholder': 'Name or email',
  'meetings.filter.dateRange': 'Meeting date',
  'meetings.filter.dateFrom': 'From date',
  'meetings.filter.dateTo': 'To date',
  'meetings.filter.platform': 'Platform',
  'meetings.status.all': 'All statuses',
  'meetings.status.ready': 'Ready',
  'meetings.status.processing': 'Processing',
  'meetings.status.recording': 'Recording now',
  'meetings.status.scheduled': 'Scheduled',
  'meetings.status.failed': 'Failed',
  'meetings.status.denied': 'Denied',
  'meetings.status.notAdmitted': 'Not Admitted',
  'meetings.status.joinFailed': "Couldn't Join",
  'meetings.status.meetingCancelled': 'Meeting Cancelled',
  'meetings.status.skipped': 'Not recorded',
  'meetings.status.noAudio': 'No audio',
  'meetings.platform.all': 'All platforms',
  'meetings.shared': 'Shared',
  'meetings.recording': 'Recording',
  'meetings.decisions': '{count} decisions',
  'meetings.actions': '{count} actions',

  /* --- meeting status badge --------------------------------------------- */
  'meetingStatus.scheduled': 'Scheduled',
  'meetingStatus.joining': 'Joining',
  'meetingStatus.recording': 'Recording',
  'meetingStatus.processing': 'Processing',
  'meetingStatus.ready': 'Ready',
  'meetingStatus.failed': 'Failed',
  'meetingStatus.skipped': 'Not recorded',
  'meetingStatus.noAudio': 'No audio',
  'meetingStatus.notAdmitted': 'Not admitted',
  'meetingStatus.denied': 'Access denied',
  'meetingStatus.joinFailed': "Couldn't join",
  'meetingStatus.meetingCancelled': 'Meeting cancelled',

  /* --- meeting detail --------------------------------------------------- */
  'meetingDetail.allMeetings': 'All meetings',
  'meetingDetail.backToSeries': 'Back to series',
  'meetingDetail.recorded': '{duration} recorded',
  'meetingDetail.meetingId': 'Meeting ID {id}',
  'meetingDetail.copyMeetingId': 'Copy meeting ID',
  'meetingDetail.toast.meetingIdCopied': 'Meeting ID copied',
  'meetingDetail.share': 'Share',
  'meetingDetail.moreActions': 'More actions',
  'meetingDetail.reRunTranscription': 'Re-run transcription',
  'meetingDetail.reSendSummaryEmail': 'Re-send summary email',
  'meetingDetail.deleteMeetingAndAudio': 'Delete meeting and audio',
  'meetingDetail.rejoin': 'Ask the bot to join again',
  'meetingDetail.rejoinHint': 'The host declined the recorder for this meeting. Asking again puts it back in the lobby.',
  'meetingDetail.toast.rejoinRequested': 'Asked the bot to join again',
  'meetingDetail.toast.rejoinRequestedBody': 'It will knock in about a minute. Somebody in the meeting has to admit it.',
  'meetingDetail.toast.rejoinAlreadyRequested': 'A recorder is already on its way back to this meeting.',
  'meetingDetail.toast.rejoinLimitReached': 'The bot has already been asked several times. Start a manual recording if it is still needed.',
  'meetingDetail.toast.rejoinMeetingOver': 'This meeting has ended, so a recorder can no longer join it.',
  'meetingDetail.toast.rejoinFailed': 'Could not ask the bot to join again',
  'meetingDetail.tab.summary': 'Summary',
  'meetingDetail.tab.transcript': 'Transcript',
  'meetingDetail.tab.cleaned': 'Cleaned',
  'meetingDetail.tab.actions': 'Action items',
  'meetingDetail.tab.participants': 'Participants',
  'meetingDetail.participantsTitle': 'Participants',
  'meetingDetail.participantsDesc': 'Talk share is measured from the attributed transcript, so an unnamed voice is excluded.',
  'meetingDetail.noEmailCaptured': 'No email captured',
  'meetingDetail.organiser': 'Organiser',
  'meetingDetail.external': 'External',
  'meetingDetail.deleteTitle': 'Delete this meeting?',
  'meetingDetail.deleteBody': 'The recording, transcript and summary are deleted permanently. Action items already pushed to other tools are not removed.',
  'meetingDetail.deletePermanently': 'Delete permanently',
  'meetingDetail.toast.summaryEmailQueued': 'Summary email queued',
  'meetingDetail.toast.queuedForReprocessing': 'Queued for re-processing',
  'meetingDetail.toast.couldNotQueueReprocessing': 'Could not queue re-processing',
  'meetingDetail.toast.shareLinkCopied': 'Share link copied',
  'meetingDetail.toast.shareLinkCopiedBody': 'Anyone with the link can read this summary.',
  'meetingDetail.toast.couldNotCreateShareLink': 'Could not create a share link',
  'meetingDetail.toast.cleaningStarted': 'Cleaning started',
  'meetingDetail.toast.cleaningStartedBody': 'It runs in the background; refresh in a minute. The recorded transcript and the summary are unchanged.',
  'meetingDetail.toast.cleaningNotEnabled': 'Cleaning is not enabled in this environment',
  'meetingDetail.toast.couldNotStartCleaning': 'Could not start cleaning',
  'meetingDetail.toast.meetingDeleted': 'Meeting deleted',
  'meetingDetail.toast.meetingDeletedBody': 'Audio, transcript and summary have been removed.',
  'meetingDetail.toast.deleteFailed': 'Could not delete the meeting',
  'meetingDetail.toast.exportFailed': 'Could not generate the meeting report',

  /* --- transcript ------------------------------------------------------- */
  'transcript.find': 'Find in transcript…',
  'transcript.searchAria': 'Search this transcript',
  'transcript.match': '{count} match',
  'transcript.matches': '{count} matches',
  'transcript.participantsCount': '{count} participants',
  'transcript.distinctVoices': '{count} distinct voices',
  'transcript.noCleanedTitle': 'No cleaned transcript',
  'transcript.noCleanedNoTranscript': 'This meeting has no transcript to clean.',
  'transcript.noCleanedNotRun': 'Mistranscription repair has not run on this meeting. Running it now reads the existing transcript and writes a cleaned copy — the recorded transcript, the summary and the action items are left exactly as they are.',
  'transcript.cleanThisTranscript': 'Clean this transcript',
  'transcript.inProgressTitle': 'Transcription in progress',
  'transcript.inProgressBody': 'The recording is being transcribed and speaker-matched. This page updates itself.',
  'transcript.noTranscriptTitle': 'No transcript',
  'transcript.noTranscriptBody': 'This meeting has no transcript. It was either not recorded, or the recording produced no speech.',
  'transcript.notice.changedNothingPrefix': 'Mistranscription repair ran on this transcript and changed ',
  'transcript.notice.nothing': 'nothing',
  'transcript.notice.lineRepaired': '{count} line',
  'transcript.notice.linesRepaired': '{count} lines',
  'transcript.notice.repairedBy': ' repaired by {model}. Each is marked, with the recorded wording beneath it.',
  'transcript.notice.repairedPlain': ' repaired. Each is marked, with the recorded wording beneath it.',
  'transcript.notice.summaryNote': ' The summary and action items are generated from the recorded transcript, not from this one.',
  'transcript.notice.runAgain': 'Run again',
  'transcript.line.lowConfidence': 'low confidence',
  'transcript.line.lowConfidenceTip': 'The speech recogniser was unsure of this line. Check it against the audio.',
  'transcript.line.repaired': 'repaired',
  'transcript.line.originallyTranscribedAs': 'Originally transcribed as: “{text}”',
  'transcript.line.playFrom': 'Play from {time}',

  /* --- summary -------------------------------------------------------------- */
  'player.play': 'Play',
  'player.pause': 'Pause',
  'player.back': 'Back {seconds} seconds',
  'player.forward': 'Forward {seconds} seconds',
  'player.mute': 'Mute',
  'player.unmute': 'Unmute',
  'player.scrub': 'Seek within the recording',
  'player.speedTip': 'Playback speed',
  'player.unavailable': 'No audio is stored for this meeting yet.',
  'player.notConfigured': 'Recording playback is not enabled on this deployment.',
  'player.mediaError': 'The recording stopped loading. Its link may have expired.',
  'player.forbidden': 'Your role does not include permission to play recordings. Someone who can manage roles \u2014 by default the workspace owner \u2014 can grant it in Settings \u2192 Roles.',
  'player.failed': 'Could not load the recording.',
  'player.retry': 'Try again',
  'player.timecodeLoading': 'The recording is still loading.',
  'player.openTranscriptAt': 'Open transcript at {time}',

  'summary.beingGeneratedTitle': 'Summary being generated',
  'summary.beingGeneratedBody': 'Decisions, action items and the narrative are produced after transcription finishes.',
  'summary.noSummaryTitle': 'No summary yet',
  'summary.noSummaryBody': 'A summary is produced once this meeting has a transcript.',
  'summary.notInEnglishTitle': 'Part of this summary is not in English',
  'summary.fieldsUntranslated': '{count} field(s) still contain untranslated text. The transcript is unaffected.',
  'summary.discussion': 'Discussion',
  'summary.generatedPrompt': 'Generated {when} · prompt {version}',
  'summary.copy': 'Copy',
  'summary.keyPoints': 'Key points',
  'summary.keyPointsDerivedDesc': 'Taken from the discussion above — this meeting was summarised before key points were produced.',
  'summary.keyPointsDerivedBadge': 'From the discussion',
  'summary.noKeyPointsTitle': 'No key points',
  'summary.noKeyPointsBody': 'The summariser produced none for this meeting, and there was no discussion to take them from.',
  'summary.decisions': 'Decisions',
  'summary.decisionsDesc': 'Each one carries the words it was made in.',
  'summary.deciderNotIdentified': 'Decider not identified',
  'summary.hideQuote': 'Hide quote',
  'summary.showQuote': 'Show quote',
  'summary.quoteTip': 'Verbatim and never translated — this is the evidence, not a paraphrase.',
  'summary.openQuestions': 'Open questions',
  'summary.openQuestionsDesc': 'Raised and not resolved in this meeting.',
  'summary.actionItems': 'Action items',
  'summary.unassigned': 'Unassigned',
  'summary.inferredSuffix': ' (inferred)',
  'summary.copied': 'Summary copied',
  'summary.sections.noTime': 'No timestamp',
  'summary.sections.playFrom': 'Play from {time}',
  'summary.copy.sectionsHeading': 'Topics:',
  'summary.copy.keyPointsHeading': 'Key points:',
  'summary.copy.keyPointsDerivedHeading': 'Key points (taken from the discussion):',
  'summary.copy.decisionsHeading': 'Decisions:',
  'summary.copy.actionItemsHeading': 'Action items:',

  /* --- attribution notice --------------------------------------------------- */
  'attribution.suspectTitle': 'Speaker names may be wrong throughout this meeting',
  'attribution.suspectBody': 'The caption and audio clocks disagree, so every name on this transcript is a guess — including the owners of its action items. Read the audio before acting on anything attributed here, and do not share this summary outside the workspace.',
  'attribution.noNamesTitle': 'No speaker names were captured',
  'attribution.noNamesBody': 'Captions never appeared in this meeting, so voices are labelled Speaker 1, Speaker 2 and so on. The words are reliable; who said them is not recorded.',
  'attribution.attributed': '{percent} attributed',
  'attribution.notAttributedTip': '{percent} of speech is not attributed to a named person.',

  /* --- speaker chip ------------------------------------------------------- */
  'speaker.unnamedWithLabel': 'Unnamed ({label})',
  'speaker.unnamed': 'Unnamed speaker',

  /* --- action items --------------------------------------------------------- */
  'actions.title': 'Action items',
  'actions.description': 'Commitments extracted from meetings, with the person who made them and the moment they did. Filter by patron to see what one company is owed.',
  'actions.status': 'Status',
  'actions.owner': 'Owner',
  'actions.status.all': 'All',
  'actions.status.open': 'Open',
  'actions.status.inProgress': 'In progress',
  'actions.status.done': 'Done',
  'actions.owner.all': 'Everyone',
  'actions.owner.me': 'Mine',
  'actions.owner.unassigned': 'Unassigned',
  'actions.noItemsTitle': 'No action items',
  'actions.noItemsBody': 'Commitments made in a meeting appear here with the person who made them. You can add one by hand if the summary missed it.',
  'actions.reopen': 'Reopen: {text}',
  'actions.markDone': 'Mark done: {text}',
  'actions.edit': 'Edit: {text}',
  'actions.unassignedNobodyTip': 'Nobody has picked this up. The model either named nobody, or named somebody we could not match to a person.',
  'actions.more': 'More…',
  'actions.createJira': 'Create Jira issue',
  'actions.postSlack': 'Post to Slack',
  'actions.addNotion': 'Add to Notion',
  'actions.moveBackToOpen': 'Move back to open',
  'actions.markInProgress': 'Mark in progress',
  'actions.dismissItem': 'Dismiss item',
  'actions.deleteItem': 'Delete item',
  'actions.addedByHand': 'added by hand',
  'actions.addedByHandTip': 'Somebody typed this in rather than the summariser extracting it from the recording.',
  'actions.inferredTip': 'This owner was matched by name similarity, not stated outright. Check it before chasing them.',
  'actions.externalTip': 'Nobody with an account here. They can see nothing of this meeting and we have sent them nothing — push the item to Jira or Slack to actually reach them.',
  'actions.editorText': 'Action item text',
  'actions.editorHint': 'Enter to save, Escape to cancel',
  'actions.overduePrefix': 'Overdue · ',
  'actions.duePrefix': 'Due ',
  'actions.setDueDate': 'Set due date',
  'actions.dueDate': 'Due date',
  'actions.clearDueDate': 'Clear due date',
  'actions.toast.sentToJira': 'Sent to Jira',
  'actions.toast.postedToSlack': 'Posted to Slack',
  'actions.toast.notionNotConnected': 'Notion is not connected',
  'actions.toast.itemChangedTitle': 'Somebody else changed this first',
  'actions.toast.itemChangedBody': 'Your edit was not saved so it could not overwrite theirs. Refresh and try again.',
  'actions.toast.notFoundTitle': 'We could not find that person',
  'actions.toast.notFoundBody': 'They are not in this meeting or this workspace. Assign by email address to give it to someone outside it.',
  'actions.toast.updateFail': 'Could not update that action item',
  'actions.toast.saveFail': 'Could not save that change',
  'actions.toast.addFail': 'Could not add that action item',
  'actions.toast.deleteFail': 'Could not delete that action item',
  'actions.toast.changeNotSaved': 'Your change was not saved. Try again.',
  'actions.toast.addedTitle': 'Action item added',
  'actions.toast.addedAssigned': 'Assigned to {name}.',
  'actions.toast.addedUnassigned': 'Nobody is assigned to it yet.',
  'actions.deepLinkTitle': 'Showing one item from a notification',
  'actions.deepLinkClear': 'View all action items',

  /* --- add action item --------------------------------------------------- */
  'addAction.add': 'Add action item',
  'addAction.placeholder': 'What needs to happen?',
  'addAction.text': 'Action item text',
  'addAction.due': 'Due',
  'addAction.selectedPerson': 'Selected person',

  /* --- assignee picker --------------------------------------------------- */
  'assignee.assign': 'Assign',
  'assignee.dialogLabel': 'Assign this action item',
  'assignee.searchPlaceholder': 'Search people, or type an email',
  'assignee.searchAria': 'Search people, or type an email address',
  'assignee.loadFail': 'Could not load the list. You can still assign by email address.',
  'assignee.group.inThisMeeting': 'In this meeting',
  'assignee.group.inTheWorkspace': 'In the workspace',
  'assignee.group.outside': 'Outside this workspace',
  'assignee.nobodyMatches': 'Nobody here matches “{query}”. Type their full email address to assign it to someone outside this workspace.',
  'assignee.outsideNoAccess': 'They get no access to this meeting and no email from us — the item just carries their name.',
  'assignee.leaveUnassigned': 'Leave unassigned',
  'assignee.assignOutside': 'Assign to someone outside this workspace',
  'assignee.typeInBox': 'Type their email address in the box above. Nothing is sent to them, and they get no access to this meeting.',
  'assignee.notInThisMeeting': 'not in this meeting',
  'assignee.unassigned': 'Unassigned',
  'assignee.inferred': 'inferred',
  'assignee.external': 'external',
  'assignee.memberNotInMeetingTitle': 'They were not on this meeting’s invite, so they did not hear this agreed.',

  /* --- ask ------------------------------------------------------------------- */
  'ask.title': 'Ask Sabha',
  'ask.description': 'Questions answered from your meetings, with the moments they came from.',
  'ask.emptyTitle': 'Ask about anything that was said',
  'ask.emptyBody': 'Answers are drawn only from meetings you have access to, and every claim is cited.',
  'ask.sources': 'Sources',
  'ask.reading': 'Reading your meetings…',
  'ask.inputPlaceholder': 'Ask about a decision, an owner, or a deadline…',
  'ask.inputAria': 'Your question',
  'ask.send': 'Ask',
  'ask.disclaimer': 'Answers can be wrong. Check the cited moment before acting on one.',
  'ask.suggestion.1': 'What did we decide about the FMS cutover?',
  'ask.suggestion.2': 'What is blocking the Northwind deal?',
  'ask.suggestion.3': 'Summarise everything Kareena committed to this week',
  'ask.suggestion.4': 'Which action items are overdue and unowned?',
  'ask.toast.failTitle': 'Could not answer that',
  'ask.toast.failBody': 'The request failed. Try again in a moment.',

  /* --- search -------------------------------------------------------------- */
  'search.title': 'Search',
  'search.description': 'Everything said in every meeting the recorder attended.',
  'search.inputPlaceholder': 'Search transcripts, titles and action items…',
  'search.tab.transcript': 'Transcript',
  'search.tab.meetings': 'Meetings',
  'search.tab.actions': 'Action items',
  'search.idleTitle': 'Search across every meeting',
  'search.idleBody': 'Try a project name, a decision you half-remember, or a person’s name. Two characters minimum.',
  'search.noSegmentsTitle': 'Nothing said about that',
  'search.noSegmentsBody': 'No transcript line contains this term. Try a shorter phrase — search is literal, not semantic. Ask Sabha handles the vaguer questions.',
  'search.noMeetings': 'No meeting titles match',
  'search.noActions': 'No action items match',

  /* --- analytics --------------------------------------------------------- */
  'analytics.title': 'Analytics',
  'analytics.description': 'How your workspace meets, and how much of it Sabha captured.',
  'analytics.dateRange': 'Date range',
  'analytics.range.7d': '7 days',
  'analytics.range.30d': '30 days',
  'analytics.range.90d': '90 days',
  'analytics.stat.meetings': 'Meetings',
  'analytics.stat.hoursCaptured': 'Hours captured',
  'analytics.stat.actionItems': 'Action items',
  'analytics.stat.avgLength': 'Avg length',
  'analytics.completed': '{percent} completed',
  'analytics.recordingVolume': 'Recording volume',
  'analytics.recordingVolumeDesc': 'Minutes captured per day.',
  'analytics.talkTime': 'Talk time',
  'analytics.talkTimeDesc': 'Attributed speech per person. Unattributed speech is shown as its own row rather than distributed.',
  'analytics.topics': 'Topics',
  'analytics.topicsDesc': 'Most discussed, by mention.',
  'analytics.languageMix': 'Language mix',
  'analytics.languageMixDesc': 'Detected per segment across all transcripts.',
  'chart.talkTime': 'Talk time',
  'chart.minutes': '{value} min',
  'chart.recorded': 'Recorded',

  /* --- integrations --------------------------------------------------------- */
  'integrations.title': 'Integrations',
  'integrations.description': 'Where meetings come from, and where their output goes.',
  'integrations.category.all': 'All',
  'integrations.category.calendar': 'Calendar & identity',
  'integrations.category.conferencing': 'Conferencing',
  'integrations.category.productivity': 'Productivity',
  'integrations.category.crm': 'CRM',
  'integrations.category.storage': 'Storage & export',
  'integrations.category.chat': 'Chat',
  'integrations.status.connected': 'Connected',
  'integrations.status.disconnected': 'Not connected',
  'integrations.status.error': 'Needs attention',
  'integrations.status.pending': 'Awaiting approval',
  'integrations.needAttention': '{count} connection needs attention',
  'integrations.needAttentionPlural': '{count} connections need attention',
  'integrations.connectedBy': 'Connected by {who} · {when}',
  'integrations.toast.connected': 'Connected',
  'integrations.toast.disconnected': 'Disconnected',
  'integrations.toast.failTitle': 'That did not work',
  'integrations.toast.failBody': 'The provider rejected the request.',

  /* --- settings: languages pane (interface strings only) ----------------- */
  'langSettings.transcriptionTitle': 'Transcription languages',
  'langSettings.transcriptionDesc': 'Applied to new recordings. Existing transcripts are not re-processed.',
  'langSettings.detectionMode': 'Detection mode',
  'langSettings.mode.identify': 'Auto-detect from the candidate list (recommended)',
  'langSettings.mode.pinned': 'Always use one language',
  'langSettings.candidateLanguages': 'Candidate languages',
  'langSettings.candidateHelp': 'Fewer candidates means faster, more accurate detection. Listing languages nobody speaks costs accuracy on the ones they do.',
  'langSettings.selectAtLeastOne': 'Select at least one language.',
  'langSettings.language': 'Language',
  'langSettings.codeSwitchTitle': 'Code-switched speech will transcribe worse',
  'langSettings.codeSwitchBody': 'A pinned language transcribes the other language phonetically — Hindi words come out as Devanagari spellings of English, or the reverse. Pin one only for meetings you know are single-language.',
  'langSettings.summaryOutput': 'Summary output',
  'langSettings.alwaysEnglish': 'Always write summaries in English',
  'langSettings.alwaysEnglishDesc': 'Transcripts stay in the language spoken. Verbatim decision quotes are never translated.',
  'langSettings.flagUntranslated': 'Flag summaries that still contain untranslated text',
  'langSettings.flagUntranslatedDesc': 'Adds a banner rather than silently shipping mixed-language output.',

  /* --- settings: notifications pane ------------------------------------------ */
  'notifSettings.recipient.organizer': 'Organiser only (safest)',
  'notifSettings.recipient.internal': 'Invitees inside your domain',
  'notifSettings.recipient.all': 'Every invitee, including external guests',
  'notifSettings.recipient.none': 'Nobody — read summaries in the app',
  'notifSettings.audience.organizer': 'the organiser of each recorded meeting',
  'notifSettings.audience.internal': 'every invitee inside your own domain, for every recorded meeting',
  'notifSettings.audience.all': 'every invitee of every recorded meeting, including people outside your domain',
  'notifSettings.audience.none': 'nobody',
  'notifSettings.deliveryTitle': 'Summary delivery',
  'notifSettings.whoReceives': 'Who receives the summary email',
  'notifSettings.whoReceivesHint': 'For many attendees this email is the first they hear of a recording. Widen it deliberately.',
  'notifSettings.send': 'Send',
  'notifSettings.send.immediate': 'As soon as the summary is ready',
  'notifSettings.send.endOfDay': 'Batched at end of day',
  'notifSettings.eventsTitle': 'Events',
  'notifSettings.eventsDesc': 'What Sabha notifies on.',
  'notifSettings.summaryEmail': 'Summary email to participants',
  'notifSettings.summaryEmailDesc': 'Sent when a meeting is ready.',
  'notifSettings.failureAlerts': 'Recording failure alerts',
  'notifSettings.failureAlertsDesc': 'To the organiser and workspace admins — not to every invitee.',
  'notifSettings.actionAssignment': 'Action item emails (Sabha Catchup)',
  'notifSettings.actionAssignmentDesc': 'One end-of-day email per person with their open items and due dates, never one per assignment. Organisers are reminded on days 1, 3 and 7 about items with no owner.',
  'notifSettings.weeklyDigest': 'Weekly workspace digest',
  'notifSettings.weeklyDigestDesc': 'Volume, open items and unowned commitments.',
  'notifSettings.confirmTitle': 'Widen who receives summary emails?',
  'notifSettings.confirmBody': 'Summaries will be emailed to {audience}. An email cannot be recalled, and for many attendees it is the first they hear that a recording existed.',
  'notifSettings.confirmButton': 'Widen recipients',

  /* --- settings: recording rules ------------------------------------------- */
  'recording.rulesInOrderTitle': 'Rules are evaluated in order, and the first match wins',
  'recording.rulesInOrderBodyPrefix': 'If no rule matches, the meeting is ',
  'recording.rulesInOrderNot': 'not',
  'recording.rulesInOrderBodySuffix': ' recorded. The recorder only ever joins meetings its mailbox was invited to, whatever these rules say.',
  'recording.rulesTitle': 'Recording rules',
  'recording.addRule': 'Add rule',
  'recording.enableRule': 'Enable rule: {name}',
  'recording.retention': 'Retention',
  'recording.retentionDesc': 'How long artefacts are kept before deletion.',
  'recording.retention.audio': 'Audio',
  'recording.retention.transcript': 'Transcript',
  'recording.retention.summary': 'Summary',
  'recording.retention.audioValue': '30 days',
  'recording.retention.transcriptValue': '12 months',
  'recording.retention.summaryValue': 'Indefinite',

  /* --- settings: security ----------------------------------------------------- */
  'security.authTitle': 'Authentication',
  'security.authDesc': 'How members sign in.',
  'security.requireSso': 'Require SSO for all members',
  'security.requireSsoDesc': 'Password sign-in is disabled once this is on.',
  'security.enforce2fa': 'Enforce two-factor for admins',
  'security.samlUrl': 'SAML metadata URL',
  'security.retentionTitle': 'Retention',
  'security.retentionDesc': 'How long each artefact is kept before deletion. Plan-level; contact support to change.',
  'security.retention.audio': 'Audio',
  'security.retention.transcript': 'Transcript',
  'security.retention.summary': 'Summary',
  'security.retention.days': '{count} days',
  'security.retention.months': '{count} months',
  'security.retention.indefinite': 'Indefinite',
  'security.exportTitle': 'Export your data',
  'security.exportDesc': 'Audio, transcripts and summaries as a single archive, delivered by an expiring link.',
  'security.requestExport': 'Request export',
  'security.dangerZone': 'Danger zone',
  'security.dangerZoneDesc': 'These actions cannot be undone.',
  'security.deleteWorkspaceAlertTitle': 'Deleting the workspace deletes every recording',
  'security.deleteWorkspaceAlertBody': 'All audio, transcripts, summaries and action items are removed permanently, for every member.',
  'security.deleteWorkspace': 'Delete this workspace',
  'security.ssoConfirmTitle': 'Require SSO for everyone?',
  'security.ssoConfirmBody': 'Password sign-in is disabled immediately. If your identity provider mapping is wrong, nobody — including you — can sign in. Verify your own SSO login in another tab first.',
  'security.ssoConfirmButton': 'Require SSO',
  'security.deleteConfirmTitle': 'Delete this workspace?',
  'security.deleteConfirmBody': 'Every recording, transcript and summary is deleted permanently for all members. This cannot be undone.',
  'security.deleteConfirmButton': 'Delete everything',
  'security.toast.deletionDisabled': 'Workspace deletion is disabled in this build',

  /* --- settings: billing ------------------------------------------------------ */
  'billing.col.period': 'Period',
  'billing.col.amount': 'Amount',
  'billing.col.status': 'Status',
  'billing.stat.plan': 'Plan',
  'billing.stat.renews': 'renews {date}',
  'billing.stat.seats': 'Seats',
  'billing.stat.minutesUsed': 'Minutes used',
  'billing.stat.ofIncluded': 'of {count} included',
  'billing.usageTitle': 'Usage this cycle',
  'billing.usageDesc': 'Recording minutes are metered on captured audio, not on scheduled meeting length.',
  'billing.changePlan': 'Change plan',
  'billing.minutesUsedLabel': 'Minutes used',
  'billing.minutesRemaining': '{count} minutes remaining. Overage is billed per minute at the end of the cycle.',
  'billing.invoices': 'Invoices',

  /* --- settings: members ---------------------------------------------------- */
  'members.col.member': 'Member',
  'members.col.role': 'Role',
  'members.col.status': 'Status',
  'members.col.lastActive': 'Last active',
  'members.changeTo': 'Change to {role}',
  'members.manage': 'Manage {name}',
  'members.resendInvitation': 'Resend invitation',
  'members.removeFromWorkspace': 'Remove from workspace',
  'members.title': 'Members',
  'members.description': 'Who belongs to this workspace, and what each of them may do.',
  'members.invitePeople': 'Invite people',
  'members.toast.updated': 'Member updated',
  'members.toast.updateFail': 'Could not update that member',
  'members.toast.invitationResent': 'Invitation resent',
  'members.invite.title': 'Invite people',
  'members.invite.description': 'Send an invitation to join this workspace.',
  'members.invite.emailLabel': 'Email address',
  'members.invite.emailPlaceholder': 'name@company.com',
  'members.invite.roleLabel': 'Role',
  'members.invite.submit': 'Send invitation',
  'members.status.active': 'active',
  'members.status.invited': 'invited',
  'members.status.suspended': 'suspended',

  /* --- settings: workspace ------------------------------------------------- */
  'workspace.title': 'Workspace',
  'workspace.description': 'Identity and defaults for everyone in this tenant.',
  'workspace.name': 'Workspace name',
  'workspace.nameHint': 'Appears in the tenant switcher and in outgoing email.',
  'workspace.verifiedDomain': 'Verified domain',
  'workspace.verifiedDomainHint': 'Anyone with this email domain can join without an invite.',
  'workspace.dataResidency': 'Data residency',
  'workspace.region.ap': 'India (Mumbai)',
  'workspace.region.eu': 'Ireland',
  'workspace.region.us': 'United States (N. Virginia)',
  'workspace.dataResidencyHint': 'Fixed at creation. Contact support to migrate a workspace.',
  'workspace.recorderName': 'Recorder display name',
  'workspace.recorderNameHint': 'Shown to participants in the meeting.',
  'workspace.disclosureTitle': 'Disclosure',
  'workspace.disclosureDesc': 'What participants are told. The calendar invite is the primary consent record.',
  'workspace.announceRecording': 'Announce the recording in meeting chat on join',
  'workspace.announceRecordingDesc': 'Posts a one-line notice when the recorder is admitted.',
  'workspace.requireExternalTold': 'Require every external participant to be told before recording starts',
  'workspace.requireExternalToldDesc': 'Skips a meeting if any invitee is outside your domain and has not accepted the notice.',

  /* --- patrons ----------------------------------------------------------------- */
  'patrons.aboutTitle': 'What a patron is for',
  'patrons.aboutBodyPrefix': 'Tag the companies you do work for, and every meeting and action item can be filtered by them. A meeting is linked ',
  'patrons.aboutAutomatically': 'automatically',
  'patrons.aboutBodyMid': ' when somebody from one of the patron’s domains was on the invite — including meetings you recorded before adding the patron. An internal discussion ',
  'patrons.aboutAbout': 'about',
  'patrons.aboutBodySuffix': ' a patron has no attendee to go on, so tag those on the meeting itself.',
  'patrons.alreadyMeetWith': 'Companies you already meet with',
  'patrons.alreadyMeetWithDesc': 'External domains from your invites that no patron covers yet. Adding one claims its past meetings too.',
  'patrons.oneMeeting': '{count} meeting',
  'patrons.manyMeetings': '{count} meetings',
  'patrons.lastMet': ' · last {date}',
  'patrons.example': ' · e.g. {name}',
  'patrons.addAsPatron': 'Add as patron',
  'patrons.addAPatron': 'Add a patron',
  'patrons.loading': 'Loading patrons…',
  'patrons.noPatronsTitle': 'No patrons yet',
  'patrons.noPatronsBody': 'Add the companies you do work for, then filter meetings and action items by them.',
  'patrons.openCount': ' · {count} open',
  'patrons.restoreTip': 'Bring this patron back into the filter list.',
  'patrons.archiveTip': 'Hide it from the filter list. Its meetings and action items are kept — a patron relationship ending does not undo the work.',
  'patrons.renameTip': 'Rename this patron',
  'patrons.renameAria': 'Rename {name}',
  'patrons.removeDomain': 'Remove {domain}',
  'patrons.addDomainPlaceholder': 'add a domain, e.g. northwind.example',
  'patrons.addDomainAria': 'Add a domain to {name}',
  'patrons.noDomainsYet': 'No domains yet, so nothing links automatically — attach this patron to meetings by hand, or add the domain their people email from.',
  'patrons.toast.added': 'Patron added',
  'patrons.toast.addFail': 'Could not add that patron',
  'patrons.toast.saveFail': 'Could not save that patron',
  'patrons.toast.nameTaken': 'A patron with that name already exists in this workspace.',
  'patrons.toast.domainAddFail': 'Could not add that domain',
  'patrons.toast.domainAdded': 'Domain added',
  'patrons.toast.domainAddedBodyOne': '{name} now covers {count} meeting, including past ones.',
  'patrons.toast.domainAddedBodyMany': '{name} now covers {count} meetings, including past ones.',
  'patrons.toast.domainRemoveFail': 'Could not remove that domain',
  'patrons.toast.domainRemoved': 'Domain removed',
  'patrons.toast.domainRemovedBody': 'Meetings it covered are no longer linked to this patron.',
  'patrons.toast.meetingPatronsFail': 'Could not update this meeting’s patrons',

  /* --- patron filter ---------------------------------------------------------- */
  'patronFilter.heading': 'Patron',
  'patronFilter.any': 'Any patron',
  'patronFilter.none': 'No patron',
  'patronFilter.fallback': 'Patron',
  'patronFilter.emptyHint': 'No patrons yet — add them in Settings',

  /* --- meeting patrons ------------------------------------------------------ */
  'meetingPatrons.none': 'No patron',
  'meetingPatrons.fromInviteTip': 'Linked automatically because somebody from {name} was on the invite. To unlink it, remove their domain from the patron in Settings.',
  'meetingPatrons.manualTip': 'Added by hand. {name} had nobody on this invite.',
  'meetingPatrons.fromInvite': 'from invite',
  'meetingPatrons.remove': 'Remove {name}',
  'meetingPatrons.addHeading': 'Add a patron',
  'meetingPatrons.addTrigger': 'Patron',
  'meetingPatrons.emptyHint': 'No patrons yet — add them in Settings',
  'meetingPatrons.allAttached': 'Every patron is already on this meeting',

  /* --- action item patron -------------------------------------------------- */
  'actionPatron.none': 'No patron',
  'actionPatron.overriddenTip': 'Set on this item, so it does not follow the meeting.',
  'actionPatron.inheritedManyTip': 'Inherited from the meeting, which covers {count} patrons — so this item counts against all of them. Set one to make it precise.',
  'actionPatron.inheritedTip': 'Inherited from the meeting.',
  'actionPatron.followMeeting': 'Follow the meeting',

  /* --- roles --------------------------------------------------------------- */
  'roles.title': 'Roles',
  'roles.description': 'What each role can see and do in this workspace.',
  'roles.newRole': 'New role',
  'roles.readOnlyTitle': 'Read only',
  'roles.readOnlyBody': 'You can see how this workspace is set up. Changing a role needs the “Manage roles” permission.',
  'roles.col.role': 'Role',
  'roles.col.meetingAccess': 'Meeting access',
  'roles.col.permissions': 'Permissions',
  'roles.col.members': 'Members',
  'roles.protectedTip': 'Only somebody who holds this role can change it, whatever permissions they have.',
  'roles.protected': 'Protected',
  'roles.access.every': 'Every meeting',
  'roles.access.own': 'Own meetings',
  'roles.access.none': 'No meetings',
  'roles.manage': 'Manage {name}',
  'roles.editPermissions': 'Edit permissions',
  'roles.deleteRole': 'Delete role',
  'roles.editor.newRole': 'New role',
  'roles.editor.editRole': 'Edit {name}',
  'roles.editor.role': 'role',
  'roles.editor.permissionCount': '{count} permission',
  'roles.editor.permissionCountPlural': '{count} permissions',
  'roles.editor.createRole': 'Create role',
  'roles.editor.saveRole': 'Save role',
  'roles.editor.name': 'Name',
  'roles.editor.namePlaceholder': 'Operations',
  'roles.editor.description': 'Description',
  'roles.editor.descriptionPlaceholder': 'What this role is for. Shown to whoever assigns it.',
  'roles.editor.builtInTitle': 'Built-in role',
  'roles.editor.builtInBodyPrefix': 'Every permission here is yours to change. The role itself cannot be deleted, and its internal key stays ',
  'roles.editor.builtInBodySuffix': ' so invitations and provisioning keep working.',
  'roles.editor.ownMeetingsOnlyTitle': 'Own meetings only',
  'roles.editor.ownMeetingsOnlyBody': 'Holders will see only the meetings they were invited to. This is the setting for staff who administer the workspace without reading its conversations.',
  'roles.editor.noAccessTitle': 'No meeting access at all',
  'roles.editor.noAccessBody': 'Holders will not see any meetings — not even ones they were invited to.',
  'roles.editor.youDoNotHold': 'You do not hold this permission',
  'roles.category.meetings': 'Meetings and recordings',
  'roles.category.actions': 'Action items',
  'roles.category.insight': 'Search and analytics',
  'roles.category.patrons': 'Patrons',
  'roles.category.workspace': 'Workspace administration',
  'roles.category.billing': 'Billing',
  'roles.toast.createFail': 'Could not create that role',
  'roles.toast.created': 'Role created',
  'roles.toast.createdBody': '{name} can now be assigned to members.',
  'roles.toast.saveFail': 'Could not save that role',
  'roles.toast.saved': 'Role saved',
  'roles.toast.savedBody': '{count} {verb} {name}.',
  'roles.toast.savedVerbOne': 'member holds',
  'roles.toast.savedVerbMany': 'members hold',
  'roles.toast.deleteFail': 'Could not delete that role',
  'roles.toast.deleted': 'Role deleted',

  /* --- tenants ------------------------------------------------------------- */
  'tenants.title': 'Tenants',
  'tenants.description': 'Every workspace on this deployment. Content is never shown here.',
  'tenants.createTenant': 'Create tenant',
  'tenants.count': '{count} tenants',
  'tenants.col.tenant': 'Tenant',
  'tenants.col.plan': 'Plan',
  'tenants.col.status': 'Status',
  'tenants.col.seats': 'Seats',
  'tenants.col.minutes': 'Minutes',
  'tenants.col.region': 'Region',
  'tenants.col.storagePrefix': 'Storage prefix',
  'tenants.col.created': 'Created',
  'tenants.openToDomain': '· open to domain',
  'tenants.region.ap': 'India',
  'tenants.region.eu': 'Ireland',
  'tenants.region.us': 'US East',
  'tenants.status.active': 'active',
  'tenants.status.onboarding': 'onboarding',
  'tenants.status.suspended': 'suspended',
  'tenants.manage': 'Manage {name}',
  'tenants.openSupportSession': 'Open support session',
  'tenants.adjustLimits': 'Adjust limits',
  'tenants.reactivate': 'Reactivate',
  'tenants.suspendTenant': 'Suspend tenant',
  'tenants.toast.supportAudited': 'Support sessions are audited',
  'tenants.toast.notBuilt': 'Not built yet',
  'tenants.toast.secondApprover': 'Requires a second approver',

  /* --- create tenant modal ---------------------------------------------------- */
  'createTenant.title': 'Create tenant',
  'createTenant.subtitle': 'Provisions the workspace and invites its owner.',
  'createTenant.region.ap': 'India (ap-south-1)',
  'createTenant.region.eu': 'Ireland (eu-west-1)',
  'createTenant.region.us': 'US East (us-east-1)',
  'createTenant.plan.trial': 'Trial',
  'createTenant.plan.team': 'Team',
  'createTenant.plan.business': 'Business',
  'createTenant.plan.enterprise': 'Enterprise',
  'createTenant.failedTitle': 'That did not work',
  'createTenant.couldNotCreate': 'The workspace could not be created.',
  'createTenant.workspaceName': 'Workspace name',
  'createTenant.primaryDomain': 'Primary domain',
  'createTenant.primaryDomainHint': 'Used to derive the storage prefix, which cannot change afterwards.',
  'createTenant.ownerEmail': 'Owner email',
  'createTenant.ownerEmailHint': 'Invited as owner. This is the only way into a new workspace.',
  'createTenant.region': 'Region',
  'createTenant.regionHint': 'Where the audio lives.',
  'createTenant.plan': 'Plan',
  'createTenant.planHint': 'Sets the seat and minute limits.',
  'createTenant.allowDomainJoin': 'Let anyone at this domain join with SSO',
  'createTenant.allowDomainJoinDesc': 'Their identity provider verifies the address; they arrive as a member, with no invitation. Off means every employee needs one — and nothing in the product emails them yet. Not available for public mail providers.',
  'createTenant.toast.provisionedTitle': '{name} is provisioned',
  'createTenant.toast.provisionedBody': '{who} can now sign in to claim the workspace.',
  'createTenant.toast.provisionedOwnerFallback': 'The owner',

  /* --- audit log --------------------------------------------------------- */
  'audit.title': 'Audit log',
  'audit.descSelf': 'Your own actions in this workspace.',
  'audit.descWorkspace': 'Every privileged action taken in this workspace.',
  'audit.searchPlaceholder': 'Actor, action or target…',
  'audit.col.when': 'When',
  'audit.col.actor': 'Actor',
  'audit.col.action': 'Action',
  'audit.col.target': 'Target',
  'audit.col.detail': 'Detail',
  'audit.col.ip': 'IP',
  'audit.filter.whose': 'Whose',
  'audit.filter.everyone': 'Everyone',
  'audit.filter.area': 'Area',
  'audit.filter.allAreas': 'All areas',
  'audit.filter.action': 'Action',
  'audit.filter.allActions': 'All actions',
  'audit.filter.from': 'From',
  'audit.filter.to': 'To',
  'audit.actorEvents': '{name} ({count})',
  'audit.selfTitle': 'You are seeing your own actions',
  'audit.selfBodyPrefix': 'Reading other people’s history needs the ',
  'audit.selfBodySuffix': ' permission — a workspace admin or owner can grant it on a role.',
  'audit.empty.filtered': 'No events match these filters.',
  'audit.empty.self': 'You have not taken any recorded action in this workspace yet.',
  'audit.empty.workspace': 'Nothing has been recorded in this workspace yet.',
  'audit.wasNamed': 'was “{name}”',

  /* --- auth -------------------------------------------------------------- */
  'login.title': 'Sign in to Sabha',
  'login.subtitle': 'Use the identity provider your workspace is set up with.',
  'login.ssoErrorTitle': 'We could not sign you in',
  'login.ssoError.directoryNotConfigured': 'Single sign-on is not set up for that email domain yet. Check the address, or ask whoever set up your workspace.',
  'login.ssoError.generic': 'Your identity provider could not complete the sign-in. Try again, and tell your administrator if it keeps happening.',
  'login.workEmail': 'Work email',
  'login.workEmailHint': 'We use this to find your organisation’s identity provider.',
  'login.continueMicrosoft': 'Continue with Microsoft',
  'login.continueGoogle': 'Continue with Google',
  'login.password': 'Password',
  'login.passwordCheck': 'Check your email and password and try again.',
  'login.genericError': 'Something went wrong. Try again in a moment.',
  'login.signInWithPassword': 'Sign in with a password',
  'onboarding.title': 'Set up Sabha',
  'onboarding.subtitle': 'Four steps, about three minutes. You can leave and come back.',
  'onboarding.step.connect': 'Connect a calendar',
  'onboarding.step.connectDesc': 'Microsoft 365 or Google Workspace',
  'onboarding.step.invite': 'Invite the recorder',
  'onboarding.step.inviteDesc': 'Add the bot to a meeting',
  'onboarding.step.rules': 'Choose what to record',
  'onboarding.step.rulesDesc': 'Set your first rule',
  'onboarding.step.done': 'Done',
  'onboarding.step.doneDesc': 'Wait for your first summary',
  'onboarding.connectTitle': 'Connect a calendar',
  'onboarding.connectDesc': 'Sabha reads only the recorder mailbox’s own calendar — never a member’s.',
  'onboarding.connectM365': 'Connect Microsoft 365',
  'onboarding.connectGoogle': 'Connect Google Workspace',
  'onboarding.connectNote': 'An admin has to approve the connection once. Nothing is recorded until you invite the recorder to a meeting.',
  'onboarding.inviteTitle': 'Invite the recorder to a meeting',
  'onboarding.inviteDesc': 'This is how consent is recorded.',
  'onboarding.copy': 'Copy',
  'onboarding.addAsAttendeeTitle': 'Add it as a normal attendee',
  'onboarding.addAsAttendeeBody': 'The recorder joins about two minutes before the start time. Every invitee can see it on the guest list, which is what makes the invite the consent record.',
  'onboarding.iHaveInvited': 'I have invited it',
  'onboarding.chooseTitle': 'Choose what to record',
  'onboarding.chooseDesc': 'You can change this at any time.',
  'onboarding.choice.explicit': 'Only meetings I explicitly invite',
  'onboarding.choice.explicitDesc': 'Nothing else is ever joined. Safest.',
  'onboarding.choice.external': 'All external meetings',
  'onboarding.choice.externalDesc': 'Customer and vendor calls, skipping internal 1:1s.',
  'onboarding.choice.everything': 'Everything on the connected calendar',
  'onboarding.choice.everythingDesc': 'Highest coverage. Tell your team first.',
  'onboarding.doneTitle': 'You are set up',
  'onboarding.doneBody': 'Your first summary lands about three minutes after the meeting ends. Until then there is nothing to see — that is expected.',
  'onboarding.goToWorkspace': 'Go to your workspace',
  'onboarding.toast.addressCopied': 'Address copied',
  'ssoCallback.title': 'Completing sign-in',
  'ssoCallback.noCode': 'The identity provider did not return a sign-in code.',
  'ssoCallback.couldNotComplete': 'Sign-in could not be completed. Please start again.',
  'ssoCallback.errorTitle': 'We could not sign you in',
  'ssoCallback.errorSuffix': ' Return to the sign-in page and try again.',
  'ssoCallback.verifying': 'Verifying your workspace account…',

  /* --- misc / error pages --------------------------------------------------- */
  'error.title': 'This screen crashed',
  'error.body': 'Nothing was lost. Reload to carry on — and send us the detail below if it keeps happening.',
  'error.reload': 'Reload',
  'error.unknown': 'Unknown error',
  'notFound.title': 'That page does not exist',
  'notFound.body': 'The link may be out of date, or the meeting may have been deleted.',
  'notFound.back': 'Back to your workspace',

  /* --- shared summary --------------------------------------------------------- */
  'shared.badge': 'Shared summary',
  'shared.participants': '{count} participants',
  'shared.discussion': 'Discussion',
  'shared.decisions': 'Decisions',
  'shared.actionItems': 'Action items',
  'shared.unassigned': 'Unassigned',
  'shared.inferredSuffix': ' (inferred)',
  'shared.noSummary': 'This meeting has no summary.',
  'shared.footer': 'Shared from a Sabha workspace. Anyone with this link can read this page.',

  /* --- auth layout marketing --------------------------------------------- */
  'authLayout.quote': 'Everyone leaves the call remembering something different. Sabha remembers all of it.',
  'authLayout.quoteFooter': 'Teams, Meet and Zoom — in whatever language the room actually speaks.',
  'authLayout.point1.title': 'Hindi, English, both',
  'authLayout.point1.detail': 'Switch mid-sentence. It keeps up.',
  'authLayout.point2.title': 'Decisions, not minutes',
  'authLayout.point2.detail': 'What was settled, and who owns the next move.',
  'authLayout.point3.title': 'Yours alone',
  'authLayout.point3.detail': 'Recorded on your terms, kept inside your workspace.',

  /* --- settings persistence hook --------------------------------------------- */
  'settings.toast.invitationSent': 'Invitation sent',
  'settings.toast.couldNotSendInvitation': 'Could not send that invitation',
  'settings.toast.couldNotUpdateRule': 'Could not update that rule',
  'settings.toast.exportQueued': 'Export queued — we will email a link when it is ready',
  'settings.toast.couldNotQueueExport': 'Could not queue that export',
  'meetings.toast.calendarNotConnectedTitle': 'The calendar is not connected',
  'meetings.toast.calendarNotConnectedBody': 'Only meetings the recorder has already claimed can be listed.',
  'meetings.toast.calendarUnreachableTitle': 'Could not reach the calendar',
  'meetings.toast.calendarUnreachableBody': 'Showing the last answer we got. Try again in a moment.',
  'meetings.toast.refreshFail': 'Could not refresh upcoming meetings',
  'feedback.open': 'Share feedback',
  'feedback.title': 'Share feedback',
  'feedback.description': 'Tell us what is working, what is not, and what you wish Sabha did.',
  'feedback.typeLabel': 'What kind of feedback is this?',
  'feedback.type.like': 'Something I like',
  'feedback.type.improve': 'Needs improvement',
  'feedback.type.feature_request': 'Feature request',
  'feedback.type.usability': 'Usability issue',
  'feedback.categoryLabel': 'Which part of Sabha?',
  'feedback.categoryPlaceholder': 'Choose an area',
  'feedback.category.recording': 'Recording and joining meetings',
  'feedback.category.transcript': 'Transcript and speakers',
  'feedback.category.summary': 'Summary and key points',
  'feedback.category.action_items': 'Action items',
  'feedback.category.search_ask': 'Search and Ask',
  'feedback.category.analytics': 'Analytics',
  'feedback.category.integrations': 'Integrations',
  'feedback.category.settings': 'Settings and roles',
  'feedback.category.other': 'Something else',
  'feedback.ratingLabel': 'How would you rate it?',
  'feedback.optional': '(optional)',
  'feedback.stars': '{count} of 5 stars',
  'feedback.commentsLabel': 'Comments',
  'feedback.commentsPlaceholder': 'What happened, or what would you like to see?',
  'feedback.commentsCount': '{used} / {max}',
  'feedback.submit': 'Send feedback',
  'feedback.rateLimited': 'You have sent a lot of feedback recently. Please try again a little later.',
  'feedback.error': 'Could not send your feedback. Please try again.',
  'feedback.toast.sent': 'Thanks for the feedback',
  'feedback.toast.sentBody': 'Your feedback has been recorded.',
}
